import { IdentityStorageDto } from "./identity-storage-dto";
import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  Logger,
  LoggerTransformer,
  ServiceBaseUrl,
  LocalStorage,
} from "@identity/utils";
import { UUID } from "@identity/symbols";

@singleton()
export class IdentityStorageClient {
  private logGroup = "identity-storage";
  private IdentityStorageKey = "identity";
  private ISUrl = this.serviceBaseUrl.get() + "identity-storage/";

  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "UserIdentity")
    private logger: Logger,
    @inject(UUID) private uuid: () => string,
    @inject(LocalStorage) private storage: LocalStorage,
    @inject(ServiceBaseUrl) private serviceBaseUrl: ServiceBaseUrl
  ) {}

  async fetchData(): Promise<IdentityStorageDto> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1000);

    return fetch(this.ISUrl, {
      mode: "cors",
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.status === 200 || response.status === 202) {
          return response.json();
        } else {
          return this.createFallbackResponse();
        }
      })
      .catch((reason) => {
        this.logger.info(
          this.logGroup,
          "Loading Identity LocalStorage data failed",
          reason
        );
        return this.createFallbackResponse();
      });
  }

  async postData(data: IdentityStorageDto): Promise<IdentityStorageDto> {
    return fetch(this.ISUrl + "identity", {
      credentials: "include",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({ ppid: data.ppid }),
    }).then((response) => response.json());
  }

  getLocalData(): IdentityStorageDto {
    return this.storage.getItem(this.IdentityStorageKey) as IdentityStorageDto;
  }

  setLocalData(data: Partial<IdentityStorageDto>): void {
    this.storage.setItem(this.IdentityStorageKey, data);
  }

  private createFallbackResponse(): IdentityStorageDto {
    return {
      timestamp: Date.now(),
      over18: false,
      synced: false,
      ppid: this.uuid(),
    };
  }
}
